import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const UserDataGrid = ({
  users,
  onEdit,
  onToggleStatus,
  onBulkAction,
  selectedUsers,
  onUserSelect,
  onSelectAll,
  departmentOptions: propDepartmentOptions = [],
}) => {
  const navigate = useNavigate();
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'Active', label: 'Active' },
    { value: 'Inactive', label: 'Inactive' },
    { value: 'Suspended', label: 'Suspended' },
    { value: 'Pending', label: 'Pending' }
  ];

  const roleOptions = [
    { value: 'all', label: 'All Roles' },
    { value: 'IT Admin', label: 'IT Admin' },
    { value: 'Asset Manager', label: 'Asset Manager' },
    { value: 'Auditor', label: 'Auditor' },
    { value: 'Standard User', label: 'Standard User' }
  ];

  const departmentOptions = useMemo(() => {
    if (propDepartmentOptions && propDepartmentOptions.length > 0) {
      const mapped = propDepartmentOptions.map(d => ({
        value: d.value,
        label: d.label,
      }));
      return [{ value: 'all', label: 'All Departments' }, ...mapped];
    }
    return [
      { value: 'all', label: 'All Departments' },
      { value: 'IT', label: 'IT' },
      { value: 'Finance', label: 'Finance' },
      { value: 'HR', label: 'HR' },
      { value: 'Operations', label: 'Operations' },
      { value: 'Marketing', label: 'Marketing' },
      { value: 'Sales', label: 'Sales' }
    ];
  }, [propDepartmentOptions]);

  const filteredAndSortedUsers = useMemo(() => {
    let filtered = users?.filter(user => {
      const matchesSearch = user?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                           user?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                           user?.department?.toLowerCase()?.includes(searchTerm?.toLowerCase());
      const matchesStatus = statusFilter === 'all' || user?.status === statusFilter;
      const matchesRole =roleFilter === 'all' || user?.role?.name === roleFilter;
      const matchesDepartment = departmentFilter === 'all' || user?.department === departmentFilter;
      
      return matchesSearch && matchesStatus && matchesRole && matchesDepartment;
    });

    if (sortConfig?.key) {
      filtered?.sort((a, b) => {
        let aValue = a?.[sortConfig?.key];
        let bValue = b?.[sortConfig?.key];
        
        if (sortConfig?.key === 'last_login') {
          aValue = aValue ? new Date(aValue) : new Date(0);
          bValue = bValue ? new Date(bValue) : new Date(0);
        }
        
        if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [users, searchTerm, statusFilter, roleFilter, departmentFilter, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig?.key === key && prevConfig?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig?.key !== columnKey) return 'ArrowUpDown';
    return sortConfig?.direction === 'asc' ? 'ArrowUp' : 'ArrowDown';
  };

  const getStatusBadge = (status) => {
    const colors = {
      'Active': 'bg-success text-success-foreground',
      'Inactive': 'bg-muted text-muted-foreground',
      'Suspended': 'bg-error text-error-foreground',
      'Pending': 'bg-warning text-warning-foreground'
    };
    return colors?.[status] || 'bg-muted text-muted-foreground';
  };

  const formatLastLogin = (date) => {
    if (!date) return 'Never';
    const now = new Date();
    const loginDate = new Date(date);
    const diffTime = Math.abs(now - loginDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Today';
    if (diffDays === 2) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays - 1} days ago`;
    return loginDate?.toLocaleDateString();
  };

  const isAllSelected = filteredAndSortedUsers?.length > 0 && 
    filteredAndSortedUsers?.every(user => selectedUsers?.includes(user?.id));

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Filters */}
      <div className="p-4 border-b border-border bg-muted/30">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="lg:col-span-2">
            <Input
              type="search"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full"
            />
          </div>
          <Select
            placeholder="Filter by status"
            options={statusOptions}
            value={statusFilter}
            onChange={setStatusFilter}
          />
          <Select
            placeholder="Filter by role"
            options={roleOptions}
            value={roleFilter}
            onChange={setRoleFilter}
          />
          <Select
            placeholder="Filter by department"
            options={departmentOptions}
            value={departmentFilter}
            onChange={setDepartmentFilter}
          />
        </div>
      </div>
      {/* Bulk Actions */}
      {selectedUsers?.length > 0 && (
        <div className="p-4 bg-primary/5 border-b border-border">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-card-foreground">
              {selectedUsers?.length} user{selectedUsers?.length > 1 ? 's' : ''} selected
            </span>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onBulkAction('activate')}
                iconName="UserCheck"
                iconPosition="left"
              >
                Activate
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onBulkAction('suspend')}
                iconName="UserX"
                iconPosition="left"
              >
                Suspend
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onBulkAction('reset-password')}
                iconName="Key"
                iconPosition="left"
              >
                Reset Password
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Data Grid */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="w-12 p-4 text-left">
                <Checkbox
                  checked={isAllSelected}
                  onChange={() => onSelectAll(filteredAndSortedUsers)}
                />
              </th>
              <th className="p-4 text-left">
                <button
                  onClick={() => handleSort('name')}
                  className="flex items-center space-x-1 font-medium text-card-foreground hover:text-primary transition-smooth"
                >
                  <span>Name</span>
                  <Icon name={getSortIcon('name')} size={14} />
                </button>
              </th>
              <th className="p-4 text-left">
                <button
                  onClick={() => handleSort('department')}
                  className="flex items-center space-x-1 font-medium text-card-foreground hover:text-primary transition-smooth"
                >
                  <span>Department</span>
                  <Icon name={getSortIcon('department')} size={14} />
                </button>
              </th>
              <th className="p-4 text-left">
                <button
                  onClick={() => handleSort('role')}
                  className="flex items-center space-x-1 font-medium text-card-foreground hover:text-primary transition-smooth"
                >
                  <span>Role</span>
                  <Icon name={getSortIcon('role')} size={14} />
                </button>
              </th>
              <th className="p-4 text-left">
                <button
                  onClick={() => handleSort('lastLogin')}
                  className="flex items-center space-x-1 font-medium text-card-foreground hover:text-primary transition-smooth"
                >
                  <span>Last Login</span>
                  <Icon name={getSortIcon('lastLogin')} size={14} />
                </button>
              </th>
              <th className="p-4 text-left">
                <button
                  onClick={() => handleSort('status')}
                  className="flex items-center space-x-1 font-medium text-card-foreground hover:text-primary transition-smooth"
                >
                  <span>Status</span>
                  <Icon name={getSortIcon('status')} size={14} />
                </button>
              </th>
              <th className="p-4 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedUsers?.map((user) => (
              <tr key={user?.id} className="border-t border-border hover:bg-muted/30 transition-smooth">
                <td className="p-4">
                  <Checkbox
                    checked={selectedUsers?.includes(user?.id)}
                    onChange={() => onUserSelect(user?.id)}
                  />
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                      <span className="text-primary-foreground font-medium text-sm">
                        {user?.name?.split(' ')?.map(n => n?.[0])?.join('')}
                      </span>
                    </div>
                    <div>
                      <div className="font-medium text-card-foreground">{user?.name}</div>
                      <div className="text-sm text-muted-foreground">{user?.email}</div>
                    </div>
                  </div>
                </td>
                <td className="p-4 text-card-foreground">{user?.department}</td>
                <td className="p-4 text-card-foreground">{user?.role?.name || 'N/A'}</td>
                <td className="p-4 text-card-foreground">{formatLastLogin(user?.last_login)}</td>
                <td className="p-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(user?.status)}`}>
                    {user?.status}
                  </span>
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEdit(user)}
                      iconName="Edit"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onToggleStatus(user)}
                      iconName={user?.status === 'Active' ? 'UserX' : 'UserCheck'}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      title="Asset Recovery & Exit Clearance"
                      onClick={() => navigate('/asset-management-dashboard?tab=recovery')}
                      iconName="ShieldCheck"
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {filteredAndSortedUsers?.length === 0 && (
        <div className="p-8 text-center">
          <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-card-foreground mb-2">No users found</h3>
          <p className="text-muted-foreground">Try adjusting your search or filter criteria.</p>
        </div>
      )}
    </div>
  );
};

export default UserDataGrid;